import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { cart as cartApi } from '../api';
import { defaultHomeContent, localized } from '../lib/homeContent';
import { getCachedHomeContentSync, loadHomeContent } from '../lib/homeContentLoader';
import NewsletterCtaButton from './public/NewsletterCtaButton';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localizedPath } from '../lib/localeRoutes';
import { resolvePublicAssetUrl } from '../lib/assetUrl';

const CartDrawer = lazy(() => import('./public/CartDrawer'));
const loadLoginModal = () => import('./public/LoginModal');
const LoginModal = lazy(loadLoginModal);

function IconImg({ src, alt, className = '' }) {
  return <img src={src} alt={alt} className={className} />;
}

const getHeaderText = (raw, language, fallback) => {
  let text = fallback;
  if (typeof raw === 'string') {
    text = language === 'en' ? fallback : raw;
  } else if (raw && typeof raw === 'object') {
    text = raw[language] || (language === 'en' ? fallback : (raw.es || fallback));
  }
  return String(text ?? '');
};

const getHeaderHref = (raw, language, fallback = '#') => {
  const href = localized(raw, language, typeof raw === 'string' ? raw : fallback);
  return typeof href === 'string' && href ? href : fallback;
};

function UserDropdown({ children, email, onLogout, logoutLabel, signedInLabel, role, customerProfileLabel, onCustomerProfileClick, buttonLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isInfluencer = role === 'INFLUENCER';
  const isAdmin = role === 'ADMIN' || role === 'COLLABORATOR';

  return (
    <div ref={ref} className="relative flex h-[42px] items-center">
      <button onClick={() => setOpen((p) => !p)} aria-label={buttonLabel} aria-expanded={open} className="flex items-center justify-center w-[42px] h-[42px] rounded-full transition-colors hover:bg-slate-100">
        {children}
      </button>
      {open && (
        <div className="absolute top-full right-0 pt-2 w-52 z-50">
          <div className="bg-white text-black p-2 rounded-xl shadow-xl">
            <div className="px-4 py-2 border-b border-slate-100 mb-2">
              <p className="text-xs text-slate-500">{signedInLabel}</p>
              <p className="text-sm font-bold truncate">{email}</p>
            </div>
            {isInfluencer && (
              <a href="/influencer/dashboard" onClick={() => setOpen(false)} className="block w-full rounded-lg px-4 py-2 text-left font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                Mi Panel
              </a>
            )}
            {isAdmin && (
              <a href="/admin/dashboard" onClick={() => setOpen(false)} className="block w-full rounded-lg px-4 py-2 text-left font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                Panel Admin
              </a>
            )}
            {!isAdmin && !isInfluencer && (
              <Link to="/my-account/" onClick={() => setOpen(false)} className="block w-full rounded-lg px-4 py-2 text-left font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                Mi Perfil
              </Link>
            )}
            {(isAdmin || isInfluencer) && onCustomerProfileClick && (
              <button onClick={() => { setOpen(false); onCustomerProfileClick(); }} className="block w-full rounded-lg px-4 py-2 text-left font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                {customerProfileLabel || 'Mi Perfil cliente'}
              </button>
            )}
            <button onClick={() => { setOpen(false); onLogout(); }} className="w-full rounded-lg px-4 py-2 text-left font-semibold text-red-500 transition-colors hover:bg-red-50">{logoutLabel}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GuestDropdown({ children, onLogout, guestSessionLabel, guestModeLabel, logoutLabel, buttonLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex h-[42px] items-center">
      <button onClick={() => setOpen((p) => !p)} aria-label={buttonLabel} aria-expanded={open} className="flex items-center justify-center w-[42px] h-[42px] rounded-full transition-colors hover:bg-slate-100">
        {children}
      </button>
      {open && (
        <div className="absolute top-full right-0 pt-2 w-48 z-50">
          <div className="bg-white text-black p-2 rounded-xl shadow-xl">
            <div className="px-4 py-2 border-b border-slate-100 mb-2">
              <p className="text-xs text-slate-500">{guestSessionLabel}</p>
              <p className="text-sm font-bold truncate">{guestModeLabel}</p>
            </div>
            <button onClick={() => { setOpen(false); onLogout(); }} className="w-full rounded-lg px-4 py-2 text-left font-semibold text-red-500 transition-colors hover:bg-red-50">{logoutLabel}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [content, setContent] = useState(getCachedHomeContentSync());
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isGuest, setIsGuest] = useState(localStorage.getItem('isGuest') === 'true');
  const { user, customer, isAuthenticated, logout, logoutCustomer } = useAuth();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    let alive = true;
    loadHomeContent().then((nextContent) => {
      if (alive) setContent(nextContent || defaultHomeContent);
    }).catch(() => {});

    const handleHomeContentUpdated = () => {
      loadHomeContent().then((nextContent) => {
        if (alive) setContent(nextContent || defaultHomeContent);
      }).catch(() => {});
    };
    window.addEventListener('home_content:updated', handleHomeContentUpdated);

    const params = new URLSearchParams(window.location.search);
    if (params.get('login') === 'true') {
      void loadLoginModal();
      setIsLoginModalOpen(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return () => {
      alive = false;
      window.removeEventListener('home_content:updated', handleHomeContentUpdated);
    };
  }, []);

  const handleCustomerLogout = async () => {
    await logoutCustomer();
    setOpen(false);
    setIsCartOpen(false);
  };

  const handleAdminLogout = async () => {
    await logout();
    setOpen(false);
    setIsCartOpen(false);
  };

  const handleCustomerProfileAccess = () => {
    if (customer) {
      window.location.href = localizedPath('/my-account', language);
      return;
    }

    openLoginModal();
  };

  const openLoginModal = () => {
    void loadLoginModal();
    setIsLoginModalOpen(true);
  };

  const handleGuestLogout = () => {
    localStorage.removeItem('isGuest');
    window.dispatchEvent(new Event('auth:guest'));
    setIsGuest(false);
    setOpen(false);
    setIsCartOpen(false);
  };

  const loadCart = async () => {
    try {
      const res = await cartApi.get();
      const items = res.item?.items || res.items || res.data || res || [];
      const arr = Array.isArray(items) ? items : [];
      const count = arr.reduce((s, it) => s + (it.quantity || 0), 0);
      setCartCount(count);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    loadCart();
    const onUpdate = () => loadCart();
    window.addEventListener('cart:updated', onUpdate);
    window.addEventListener('storage', onUpdate);
    
    const onOpen = () => setIsCartOpen(true);
    window.addEventListener('cart:open', onOpen);
    
    const onGuest = () => setIsGuest(localStorage.getItem('isGuest') === 'true');
    window.addEventListener('auth:guest', onGuest);
    
    return () => {
      window.removeEventListener('cart:updated', onUpdate);
      window.removeEventListener('storage', onUpdate);
      window.removeEventListener('cart:open', onOpen);
      window.removeEventListener('auth:guest', onGuest);
    };
  }, []);

  const userIcon = (
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#1C1C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );

  return (
    <>
      <div className="bg-black text-white text-center px-4 py-2 text-[11px] md:text-[12px] font-medium tracking-wide">
        {getHeaderText(content.header?.announcement, language, t('header.announcement'))}{' '}
        <NewsletterCtaButton
          source="header_announcement"
          label={getHeaderText(content.header?.announcementCtaText, language, t('header.announcementCta'))}
          className="font-black text-[#FF50A3] hover:text-white transition-colors"
        />
      </div>

      <header className="sticky top-0 z-50 bg-[#FAFAFA]">
        <div className="mx-auto flex h-[70px] max-w-[1440px] items-center justify-between px-4 md:px-6 lg:px-[45px]">
          <div className="flex h-[42px] items-center gap-4 lg:gap-10">
            <a href={localizedPath('/', language)} className="shrink-0 flex h-[42px] items-center">
              <img src={resolvePublicAssetUrl(content.header?.logoUrl || '/images/logo.png')} alt="MiskiPOP" width={211} height={62} className="h-[42px] w-auto object-contain" />
            </a>

            <nav className="hidden xl:flex items-center gap-6 text-[14px] font-semibold text-[#3B3B3B]">
              {(content.header?.navLinks || []).map((link) => {
                const href = getHeaderHref(link.href, language, '#');
                const toPath = (!href || href === '#') ? '/' : (href === '/' ? '/' : href.replace(/\/$/, ''));
                return (
                  <Link key={`${localized(link.label, language, '')}-${href}`} to={toPath} className="hover:text-[#FF50A3] transition-colors">
                    {localized(link.label, language, '')}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="hidden md:flex h-[42px] items-center gap-3 lg:gap-5">
            <label className="hidden lg:flex h-[42px] w-[289px] items-center gap-3 rounded-[50px] border border-[#C4C4C4] bg-white px-4 text-[#A3A3A3]">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input type="text" aria-label={t('common.searchProducts')} placeholder={localized(content.header?.searchPlaceholder, language, t('common.searchProducts'))} className="bg-transparent outline-none flex-1 text-[#1C1C1C] text-[14px] font-medium placeholder:text-[#666666]" />
            </label>

            <div className="flex h-[42px] items-center gap-2 lg:gap-3">
              <div className="flex h-[42px] items-center rounded-full border border-slate-200 bg-white p-0.5 text-[11px] font-black text-slate-500">
                {['es', 'en'].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setLanguage(lang)}
                    aria-label={lang === 'es' ? 'Español' : 'English'}
                    className={`rounded-full px-2 py-1 uppercase transition-colors ${language === lang ? 'bg-input-pink text-white' : 'hover:text-input-pink'}`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              {customer ? (
                <UserDropdown
                  email={customer.name || customer.email}
                  onLogout={handleCustomerLogout}
                  logoutLabel={t('header.logout')}
                  signedInLabel={t('header.signedIn')}
                  buttonLabel={t('common.account', 'Mi cuenta')}
                >
                  {userIcon}
                </UserDropdown>
              ) : user ? (
                <UserDropdown
                  email={user?.email}
                  onLogout={handleAdminLogout}
                  logoutLabel={t('header.logout')}
                  signedInLabel={t('header.signedIn')}
                  buttonLabel={t('common.account', 'Mi cuenta')}
                  role={user?.role}
                  customerProfileLabel={customer ? 'Mi Perfil cliente' : 'Iniciar sesión cliente'}
                  onCustomerProfileClick={handleCustomerProfileAccess}
                >
                  {userIcon}
                </UserDropdown>
              ) : isGuest ? (
                <GuestDropdown
                  onLogout={handleGuestLogout}
                  guestSessionLabel={t('header.guestSession')}
                  guestModeLabel={t('header.guestMode')}
                  logoutLabel={t('header.logout')}
                  buttonLabel={t('common.account', 'Mi cuenta')}
                >
                  {userIcon}
                </GuestDropdown>
              ) : (
                <button onClick={openLoginModal} onPointerDown={loadLoginModal} onMouseEnter={loadLoginModal} onFocus={loadLoginModal} aria-label={t('common.account', 'Mi cuenta')} className="flex h-[42px] w-[42px] items-center justify-center rounded-full transition-colors hover:bg-slate-100">
                  {userIcon}
                </button>
              )}
              <button onClick={() => setIsCartOpen(true)} aria-label={t('cart.title')} className="relative flex h-[42px] w-[42px] items-center justify-center rounded-full transition-colors hover:bg-slate-100">
                <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#1C1C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                {cartCount > 0 && <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-[#FF50A3] px-2 py-0.5 text-[10px] font-bold leading-none text-white">{cartCount}</span>}
              </button>
            </div>
          </div>


          <div className="flex items-center gap-2 md:hidden">
            <button onClick={() => setIsCartOpen(true)} aria-label={t('cart.title')} className="relative flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-slate-100">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#1C1C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {cartCount > 0 && <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-[#FF50A3] px-2 py-0.5 text-[10px] font-bold leading-none text-white">{cartCount}</span>}
            </button>
            <button onClick={() => setOpen(true)} aria-label={language === 'en' ? 'Open menu' : 'Abrir menú'} className="flex h-10 w-10 items-center justify-center rounded-full p-2 text-slate-900 transition-colors hover:bg-slate-100">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {open && (
        <div className="fixed inset-0 z-[9999] bg-black/60 md:hidden" onClick={() => setOpen(false)}>
          <div className="absolute top-0 right-0 h-full w-[280px] sm:w-72 bg-white p-5 sm:p-6 flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-6">
              <button onClick={() => setOpen(false)} className="text-slate-900 p-1 rounded-full hover:bg-slate-100 transition-colors">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex flex-col gap-4">
              {(content.header?.navLinks || []).map((link) => {
                const href = getHeaderHref(link.href, language, '#');
                const toPath = (!href || href === '#') ? '/' : (href === '/' ? '/' : href.replace(/\/$/, ''));
                return (
                  <Link key={`${localized(link.label, language, '')}-${href}`} to={toPath} onClick={() => setOpen(false)} className="font-bold text-slate-900 transition-colors hover:text-[#FF50A3]">
                    {localized(link.label, language, '')}
                  </Link>
                );
              })}

              {customer ? (
                <div className="mt-4 border-t border-slate-200 pt-4 flex flex-col gap-4">
                  <div className="text-[12px] font-semibold text-slate-500 uppercase">{customer.name || customer.email}</div>
                  <Link to="/my-account" onClick={() => setOpen(false)} className="font-bold text-slate-900 transition-colors hover:text-[#FF50A3]">Mi Perfil</Link>
                  <Link to="/my-account/orders" onClick={() => setOpen(false)} className="font-bold text-slate-900 transition-colors hover:text-[#FF50A3]">Mis Pedidos</Link>
                  <button onClick={handleCustomerLogout} className="text-left font-bold text-red-500 transition-colors hover:text-red-600">{t('header.logout', 'Cerrar sesión')}</button>
                </div>
              ) : user ? (
                <div className="mt-4 border-t border-slate-200 pt-4 flex flex-col gap-4">
                  <div className="text-[12px] font-semibold text-slate-500 uppercase">{user.email}</div>
                  <a href={user.role === 'INFLUENCER' ? '/influencer/dashboard' : '/admin/dashboard'} onClick={() => setOpen(false)} className="font-bold text-slate-900 transition-colors hover:text-[#FF50A3]">Mi Panel</a>
                  <button onClick={() => { setOpen(false); handleCustomerProfileAccess(); }} className="text-left font-bold text-slate-900 transition-colors hover:text-[#FF50A3]">{customer ? 'Mi Perfil cliente' : 'Iniciar sesión cliente'}</button>
                  <button onClick={handleAdminLogout} className="text-left font-bold text-red-500 transition-colors hover:text-red-600">{t('header.logout', 'Cerrar sesión')}</button>
                </div>
              ) : isGuest ? (
                <div className="mt-4 border-t border-slate-200 pt-4 flex flex-col gap-4">
                  <div className="text-[12px] font-semibold text-slate-500 uppercase">{t('header.guestMode', 'Modo Invitado')}</div>
                  <button onClick={() => { setOpen(false); openLoginModal(); }} onPointerDown={loadLoginModal} onMouseEnter={loadLoginModal} onFocus={loadLoginModal} className="text-left font-bold text-slate-900 transition-colors hover:text-[#FF50A3]">{t('common.account', 'Mi cuenta')}</button>
                  <button onClick={handleGuestLogout} className="text-left font-bold text-red-500 transition-colors hover:text-red-600">{t('header.logout', 'Cerrar sesión')}</button>
                </div>
              ) : (
                <button onClick={() => { setOpen(false); openLoginModal(); }} onPointerDown={loadLoginModal} onMouseEnter={loadLoginModal} onFocus={loadLoginModal} className="text-slate-900 font-bold mt-4 border-t border-slate-200 pt-4 text-left hover:text-[#FF50A3] transition-colors">{t('common.account', 'Mi cuenta')}</button>
              )}
            </nav>
            <div className="mt-auto pt-6 border-t border-slate-200">
              <div className="flex rounded-full border border-slate-200 bg-white p-0.5 text-[11px] font-black text-slate-500 w-fit">
                {['es', 'en'].map((lang) => (
                  <button key={lang} type="button" onClick={() => setLanguage(lang)} aria-label={lang === 'es' ? 'Español' : 'English'} className={`rounded-full px-3 py-1 uppercase ${language === lang ? 'bg-input-pink text-white' : ''}`}>{lang}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Cart Drawer */}
      {isCartOpen ? (
        <Suspense fallback={null}>
          <CartDrawer isOpen onClose={() => setIsCartOpen(false)} />
        </Suspense>
      ) : null}
      {/* Login Modal */}
      {isLoginModalOpen ? (
        <Suspense fallback={null}>
          <LoginModal isOpen onClose={() => setIsLoginModalOpen(false)} />
        </Suspense>
      ) : null}
    </>
  );
}

export default Header;
